import React, { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "store/reducers";
import {
  createLocalVideoTrack,
  createLocalAudioTrack,
  CreateLocalTrackOptions,
  LocalDataTrack,
} from "twilio-video";
import {
  toggleVideo,
  toggleAudio,
  setLocalVideoTrack,
  setLocalAudioTrack,
  setLocalDataTrack,
  setBusy,
} from "store/local-tracks/slice";
import { Icon } from "components/shared/icon/Icon";
import styles from "./Preview.module.css";
import { setErrorNotification } from "store/errors/slice";
import { ErrorType } from "store/errors/types";
import { ErrorVideoCover } from "../error/ErrorVideoCover";
import { AudioLevelMeter } from "./AudioLevelMeter";
import { getBrowser } from "utils";
import { isMobile } from "react-device-detect";
import { NetworkQuality } from "store/user/types";
import { toggleNetworkHelpDialog } from "store/user/slice";
import rollbar from "helpers/rollbar";

export const Preview: React.FC = () => {
  const dispatch = useDispatch();
  const {
    videoTrackInfo,
    audioTrackInfo,
    localAudioTrack,
    localVideoTrack,
    hideVideo,
    muteAudio,
  } = useSelector((s: RootState) => s.localTracks);
  const { error } = useSelector((s: RootState) => s.errors);
  const { networkQuality } = useSelector((s: RootState) => s.user);
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const getIndicatorLightColor = (networkQuality?: NetworkQuality) => {
    switch (networkQuality) {
      case NetworkQuality.Excellent:
        return "#33cc33";
      case NetworkQuality.Good:
        return "#99ff33";
      case NetworkQuality.Fair:
        return "#ffff00";
      case NetworkQuality.Poor:
      case NetworkQuality.Error:
        return "#cc3300";
      case undefined:
      default:
        return "gray";
    }
  };

  useEffect(() => {
    async function createPreviewTrack() {
      if (videoTrackInfo && videoRef.current && networkQuality) {
        if (videoTrackInfo?.name !== localVideoTrack?.name) {
          let options: CreateLocalTrackOptions = {
            name: videoTrackInfo?.name,
            deviceId: videoTrackInfo?.deviceId,
            aspectRatio: 1.7777777778,
            width: { ideal: 1280 },
            height: { ideal: 720 },
          };

          if (networkQuality === NetworkQuality.Fair) {
            options.width = { ideal: 854 };
            options.height = { ideal: 480 };
          }

          if (networkQuality === NetworkQuality.Poor) {
            options.width = { ideal: 640 };
            options.height = { ideal: 360 };
          }

          try {
            dispatch(setBusy({ video: true }));

            if (localVideoTrack) {
              localVideoTrack.stop();
              localVideoTrack.detach();
            }

            if (/Firefox/.test(getBrowser().name || "")) {
              await navigator.mediaDevices.getUserMedia({
                video: { deviceId: { exact: videoTrackInfo?.deviceId } },
              });
            }

            if (isMobile) {
              if (/back/.test(videoTrackInfo?.label.toLowerCase() || "")) {
                options.facingMode = "environment";
              } else if (
                /front/.test(videoTrackInfo?.label.toLowerCase() || "")
              ) {
                options.facingMode = "user";
              }
            }

            const newVideoTrack = await createLocalVideoTrack(options);

            dispatch(setLocalVideoTrack(newVideoTrack));
          } catch (err) {
            dispatch(
              setErrorNotification({
                name: err.name,
                type: ErrorType.MediaError,
                message: err.message,
              })
            );
            rollbar.error(err, { videoTrackInfo: options });
          } finally {
            dispatch(setBusy({ video: false }));
          }
        }
      }
    }

    createPreviewTrack();
  }, [dispatch, videoTrackInfo, localVideoTrack, hideVideo, networkQuality]);

  useEffect(() => {
    async function createPreviewAudioTrack() {
      if (
        audioTrackInfo?.deviceId &&
        audioTrackInfo.label !== localAudioTrack?.mediaStreamTrack.label
      ) {
        try {
          dispatch(setBusy({ audio: true }));
          if (localAudioTrack) {
            localAudioTrack.stop();
            localAudioTrack.detach();
          }

          const newAudioTrack = await createLocalAudioTrack({
            deviceId: audioTrackInfo.deviceId,
          });

          dispatch(setLocalAudioTrack(newAudioTrack));
        } catch (err) {
          dispatch(
            setErrorNotification({
              name: err.name,
              type: ErrorType.MediaError,
              message: err.message,
            })
          );
          rollbar.error(err, { audioTrackInfo: audioTrackInfo });
        } finally {
          dispatch(setBusy({ audio: false }));
        }
      }
    }

    createPreviewAudioTrack();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, audioTrackInfo, muteAudio]);

  useEffect(() => {
    async function createPreviewDataTrack() {
        try {
          const dataTrack = new LocalDataTrack();
          dispatch(setLocalDataTrack(dataTrack));
        } catch (err) {
          setErrorNotification({
            name: err.name,
            type: ErrorType.MediaError,
            message: err.message,
          });
        }
    }
    createPreviewDataTrack();
  }, [dispatch]);

  useEffect(() => {
    if (localVideoTrack && videoRef.current) {
      localVideoTrack.attach(videoRef.current);
    }

    return () => {
      localVideoTrack?.detach();
    };
  }, [localVideoTrack]);

  useEffect(() => {
    if (localAudioTrack && audioRef.current) {
      localAudioTrack.attach(audioRef.current);
    }

    return () => {
      localAudioTrack?.detach();
    };
  }, [localAudioTrack]);

  return (
    <>
      <div
        className={`${styles["preview-wrapper"]} ${
          isMobile ? styles["preview-wrapper-portrait"] : ""
        } mx-auto`}
      >
        {hideVideo && (
          <div className={styles["hidden-video-cover"]}>
            <p>Your video will be hidden</p>
          </div>
        )}
        {error && error.type === ErrorType.MediaError && (
          <ErrorVideoCover error={error} />
        )}
        <div
          className={`${styles["preview-container"]} ${
            isMobile ? styles["preview-container-portrait"] : ""
          } ${
            error && error.type === ErrorType.MediaError ? styles["hidden"] : ""
          } mb-3`}
        >
          <div
            className={`${styles["preview-element"]} ${
              /back/.test(videoTrackInfo?.label.toLowerCase() || "")
                ? ""
                : styles["mirror"]
            }`}
          >
            <video ref={videoRef} autoPlay={true} />
          </div>
        </div>
        <div className={styles["network-quality"]}>
          <span
            className={`${styles["indicator-light"]} mr-1`}
            style={{ backgroundColor: getIndicatorLightColor(networkQuality) }}
          ></span>
          <small className="mr-1">
            {networkQuality ? (
              networkQuality !== NetworkQuality.Error ? (
                <>{`Network quality is ${networkQuality}`}</>
              ) : (
                "Network Error."
              )
            ) : (
              "Checking network quality..."
            )}
          </small>
          {(networkQuality === NetworkQuality.Poor ||
            networkQuality === NetworkQuality.Fair) && (
            <span onClick={() => dispatch(toggleNetworkHelpDialog())}>
              <Icon id={"info"} className={styles["network-icon"]} />
            </span>
          )}
        </div>
        <div className={`${styles["controls"]}`}>
          <button
            type="button"
            className={`${hideVideo ? styles["disabled"] : ""} mr-1`}
            onClick={() => dispatch(toggleVideo(!hideVideo))}
          >
            <Icon id={"video-camera"} />
            <span className={styles["slash"]}>
              <Icon id={"slash"} />
            </span>
          </button>
          <button
            type="button"
            className={muteAudio ? styles["disabled"] : ""}
            onClick={() => dispatch(toggleAudio(!muteAudio))}
          >
            {localAudioTrack && !muteAudio && (
              <AudioLevelMeter audioTrack={localAudioTrack} />
            )}
            <Icon id={"microphone"} />
            <span className={styles["slash"]}>
              <Icon id={"slash"} />
            </span>
          </button>
        </div>
      </div>

      <audio ref={audioRef} autoPlay={true} muted={true} />
    </>
  );
};
